import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { CalendarState } from '../../controller';
import { MemoizedViewModalFactory } from '../viewModel';
import {
  createBookingDetailsPreferences,
  BookingDetailsPreferences,
} from './bookingDetailsPreferences';
import {
  CalendarSummaryViewModel,
  createCalendarSummaryViewModel,
} from './calendarSummary/calendarSummaryViewModel';
import { getBookingPreferencesForSelectedTime } from '../../../../utils/bookingPreferences/bookingPreferencesForSelectedTime';
import { CalendarErrors } from '../../../../types/types';
import { getFurthestValidUntilPlan } from '../../../../utils/pricingPlans/pricingPlans';
import {
  formatRfcTimeStringToDateAndTimeView,
  getDateTimeFromLocalDateTime,
} from '../../../../utils/dateAndTime/dateAndTime';

export { createDummyBookingDetailsViewModel } from './dummyBookingDetailsViewModel';

export type BookingDetailsViewModel = {
  preferences?: BookingDetailsPreferences;
  calendarSummary?: CalendarSummaryViewModel;
  alert?: string;
};

export const memoizedBookingDetailsViewModel: MemoizedViewModalFactory<BookingDetailsViewModel> =
  {
    dependencies: {
      state: [
        'servicesInView',
        'serviceVariantsMap',
        'selectableSlotsAtSelectedTime',
        'selectedTime',
        'selectedBookingPreferences',
        'calendarErrors',
        'purchasedPricingPlans',
        'selectedVariantsOptions',
      ],
      settings: [
        'videoConferenceBadge',
        'bookingDetailsPricingPlanText',
        'waitlistIndication',
        'bookingDetailsClearText',
        'preferencesTitle',
        'nextButton',
        'locationLabel',
        'staffMemberLabel',
        'durationLabel',
      ],
      subDependencies: [],
    },
    createViewModel: createBookingDetailsViewModel,
  };

export const getVideoConferencingBadge = (context: CalendarContext) => {
  const { getContent, settingsParams } = context;
  return getContent({
    settingsParam: settingsParams.videoConferenceBadge,
    translationKey: 'app.settings.defaults.video-conference-badge-text',
  });
};

export function createBookingDetailsViewModel({
  state,
  context,
}: ViewModelFactoryParams<
  CalendarState,
  CalendarContext
>): BookingDetailsViewModel {
  const baseBookingPreferences = getBookingPreferencesForSelectedTime({
    selectableSlotsAtSelectedTime: state.selectableSlotsAtSelectedTime ?? [],
    selectedBookingPreferences: state.selectedBookingPreferences,
    calendarErrors: state.calendarErrors,
    context,
    state,
  });

  let alert: string | undefined;
  if (
    state.calendarErrors.includes(CalendarErrors.NO_VALID_PRICING_PLAN_WARNING)
  ) {
    const furthestValidUntilPlan = getFurthestValidUntilPlan(
      state.purchasedPricingPlans,
    );
    alert = furthestValidUntilPlan
      ? context.t('app.booking-details.pricing-plan-alert', {
          planName: furthestValidUntilPlan.planName,
          planDate: getDateTimeFromLocalDateTime(
            furthestValidUntilPlan.validUntil!,
          ),
        })
      : undefined;
  }
  const dateRegionalSettingsLocale =
    context.languageSettings!.dateRegionalSettingsLocale!;

  const dateAndTime = state.selectedTime
    ? formatRfcTimeStringToDateAndTimeView(
        state.selectedTime,
        dateRegionalSettingsLocale,
      )
    : undefined;

  return {
    preferences: createBookingDetailsPreferences({
      state,
      context,
      preferences: baseBookingPreferences,
      dateAndTime,
    }),
    calendarSummary: createCalendarSummaryViewModel({
      context,
      state,
      baseBookingPreferences,
      dateAndTime,
    }),
    alert,
  };
}
